import React, { useState, useEffect } from 'react';
import IconButton from '@material-ui/core/IconButton';
import EyeIcon from 'mdi-material-ui/EyeCircle';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { CSVLink } from 'react-csv';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import { useHistory } from 'react-router-dom';

import { isUserRole } from '../../utils/user';
import PropertiesTable from '../../components/Tables/Properties';
import { setSelectedProperty } from '../../store/features/propertiesTable';
import PropertyDetails from '../../components/Dialogs/Property';
import * as constants from '../../constants';
import isCardinalActive from '../../utils/isCardinalActive';

const csvHeaders = [
  { label: 'Display Name', key: 'displayName' },
  { label: 'Address', key: 'address' },
  { label: 'Variant', key: 'variant' },
  { label: 'DARs Required', key: 'darsRequired' },
  { label: 'VIP Status', key: 'cardinal' },
];

const PropertiesContent = () => {
  const [ data, setData ] = useState([]);
  const [ csvData, setCsvData ] = useState({});
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const propertiesById = useSelector(state => state.properties.byId);
  const selectedProperty = useSelector(state => {
    if(!state.propertiesTable.selectedProperty) return null;
    else return propertiesById[state.propertiesTable.selectedProperty];
  });
  const selectedProperties = useSelector(state => state.propertiesTable.selectedProperties);

  useEffect(() => {
    const tempData = [];
    for(const property of Object.values(propertiesById)) {
      if(property.isDeleted) continue;
      tempData.push({
        displayName: property.displayName,
        address: property.place.formattedAddress,
        variant: property.variant ? constants.property.variantText[property.variant] : constants.property.variantText.NONE,
        state: property.place.addressComponents.state,
        city: property.place.addressComponents.city,
        darsRequired: property.patrolWindows.reduce((acc, window) => acc + window.darsRequired, 0),
        cardinal: isCardinalActive(property.cardinal) ? 'Yes' : 'No',
        id: property.propertyId,
        actions: actions(property.propertyId)
      });
    }
    setData(tempData);
  },[ propertiesById ]);

  useEffect(() => {
    setCsvData(selectedProperties.map(id => {
      const property = propertiesById[id];
      return {
        displayName: property.displayName,
        address: property.place.formattedAddress,
        variant: property.variant ? constants.property.variantText[property.variant] : constants.property.variantText.NONE,
        darsRequired: property.patrolWindows.reduce((acc, window) => acc + window.darsRequired, 0),
        cardinal: isCardinalActive(property.cardinal) ? 'Yes' : 'No',
      }
    }));
  }, [selectedProperties]);

  const actions = (id) => {
    return (
      <span className={classes.actions}>
        <IconButton onClick={() => {
          dispatch(setSelectedProperty({ selectedProperty: id }))
        }}>
          <EyeIcon />
        </IconButton>
      </span>
    )
  }

  const isAdmin = isUserRole({ admin: true });

  const handleCreateProperty = () => history.push('/map/properties/create');

  return (
    <div className={classes.root}>
      <div className={classes.scrollable}>
        <Paper className={classes.paper}>
          <List>
            <ListItem>
              <ListItemText 
                primary='Properties'
              />  
              {
                isAdmin && (
                  <ListItemSecondaryAction>
                    <Button
                      onClick={handleCreateProperty}
                      color='primary'
                      variant='contained'
                    >
                      Create
                    </Button>
                  </ListItemSecondaryAction>
                )
              }
            </ListItem>
            <ListItem>
              <PropertiesTable
                data={data}
              />
            </ListItem>
            <ListItem>
              <ListItemSecondaryAction>
                <Button
                  color='primary'
                  variant='contained'
                  disabled={Object.values(csvData).length === 0}
                >
                  {
                    Object.values(csvData).length > 0 ? (
                      <CSVLink 
                        className={classes.link} 
                        headers={csvHeaders} 
                        data={csvData} 
                        filename={`properties_export.csv`}
                      >
                        { `Export ${Object.values(csvData).length}` }
                      </CSVLink>
                    ) : (
                      'Export 0'                      
                    )
                  }
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </Paper>
      </div>
      {
        Boolean(selectedProperty) && (
          <PropertyDetails 
            property={selectedProperty}
            isOpen={Boolean(selectedProperty)}
            onClose={() => dispatch(setSelectedProperty({ selectedProperty: null }))}
          />
        )
      }
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    position: 'relative'
  },
  scrollable: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
    overflowX: 'hidden',
    overflowY: 'auto'
  },
  exportContainer: {
    display: 'flex', 
    justifyContent: 'flex-end' 
  },
  link: {
    color: theme.palette.common.white,
    textDecoration: 'none'
  },
  paper: {
    margin: theme.spacing(3),
    padding: theme.spacing(3)
  },
  buttonContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end'
  }
}));

export default PropertiesContent;